import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { platformServicesMasterActions } from '../../../store/reducers/platform-master';
import MasterComponent from '../../../common/MasterComponent';
import Popup from '../../../components/Popup';
import { ReactComponent as AlertIcon } from '../../../assets/icons/main/alertIcon.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import useNotification from '../../../common/notification';
import { defaultFields } from './form';
import { Stack, Tooltip, Typography } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../utils/table-utils';
import AddActivity from './addEditActivity';
import { maintenanceManagementActions } from '../../../store/reducers/maintenance-management';
import { MaintenanceManagementPayloadTypes } from '../../../store/models/machinery-management';
import { pageActions } from '../../../store/reducers/page-actions';
import dayjs from 'dayjs';
import { palette } from '../../../theme/palette';

interface ActivityListTypes {
  activities: any;
  activityListLoading: boolean;
  activityListTotalCount: number;
  activityListPageNumber: number;
  deleteActivityLoading: boolean;
  deleteActivitySuccess: boolean;
}

const ActivityList = ({
  activities,
  activityListLoading,
  activityListTotalCount,
  activityListPageNumber,
  deleteActivityLoading,
  deleteActivitySuccess,
}: ActivityListTypes) => {
  const dispatch = useDispatch();
  const [popup, setPopup] = useState({ open: false, rowId: null });
  const [searchValue, setSearchValue] = useState<any>('')
  const [deletableActivity, setDeletableActivity] = useState('')
  const navigate = useNavigate();
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const { pathname } = useLocation();
  const dispatchActivityListRequest = (payload: MaintenanceManagementPayloadTypes) => {
    dispatch(
      maintenanceManagementActions.getActivityListRequest({
        search_item: searchValue,
        ...sortingModel,
        ...payload,
      })
    );
  };

  useEffect(() => {
    dispatchActivityListRequest({
      limit: DEFAULT_PAGE_SIZE,
      page: DEFAULT_PAGE_NUMBER,
    });
  }, []);

  useEffect(() => {
    if (deleteActivitySuccess) {
      dispatchActivityListRequest({
        limit: DEFAULT_PAGE_SIZE,
        page:
          deleteActivitySuccess && activities.length === 1 && activityListPageNumber !== 1
            ? activityListPageNumber - 1
            : activityListPageNumber,
      });
    }
  }, [deleteActivitySuccess]);

  const columns: any = [
    {
      field: 'checklist_name',
      headerName: 'Checklist name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.value} placement='bottom' arrow>
            <Typography
              component='p'
              variant='subtitle1'
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'created_date',
      headerName: 'Created/Updated Date',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => {
        const UTCToLocal = dayjs.utc(params.row.modified_date || params.value).local().format('MMM D, YYYY hh:mm A');
        return (
          <Typography
            component='p'
            variant='subtitle1'
            sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
          >
            {UTCToLocal}
          </Typography>
        );
      },
    },
  ];

  const onClosePopup = () => {
    setPopup({
      open: false,
      rowId: null,
    });
  };

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/').filter(Boolean);
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index != 2)
          .map((item: any, index: number) => {
            if (index == 0) {
              return { route: pathnamesTemp[0], pathName: 'Maintenance Management', navigate: true };
            } else if (index == 1) {
              return { route: '', pathName: 'Maintenance Activity', navigate: false };
            } else {
              return { route: '', pathName: item, navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [activities])

  return (
    <>
      <MasterComponent
        heading='List of Activity Checklists'
        isLoading={activityListLoading || deleteActivityLoading}
        rows={activities}
        columns={columns}
        isServerPagination
        totalRowCount={activityListTotalCount}
        pageNumber={activityListPageNumber}
        onSorting={(sortModel: { field: string; sort: string }) => {
          dispatchActivityListRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            ...sortModel,
          });
          setSortingModel(sortModel);
        }}
        onPagination={(pageSize: number, pageNumber: number) => {
          dispatchActivityListRequest({
            limit: pageSize,
            page: pageNumber,
          });
        }}
        // setIsModalOpen={setIsModalOpen}
        noRowsOverlay={() => (
          <Stack height='100%' alignItems='center' justifyContent='center'>
            Checklist not available
          </Stack>
        )}
        actionsColumn={{
          edit: {
            onClick: (params: GridValueGetterParams) => {
              navigate(`edit/${params.row.checklist_id}`);
            },
          },
          visible: {
            onClick: (params: GridValueGetterParams) => {
              navigate(`view/${params.row.checklist_id}`);
            },
          },
          delete: {
            onClick: (params: GridValueGetterParams) => {
              setDeletableActivity(params.row.checklist_name);
              setPopup({
                open: true,
                rowId: params.row.checklist_id,
              });
            },
          },
        }}
        showSearch
        searchPlaceholder='Search'
        onSearch={(value: string) => {
          dispatchActivityListRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            search_item: value,
          });
          setSearchValue(value);
          dispatch(maintenanceManagementActions.onSearchForActivityItem(searchValue));
        }}
        otherProps={{
          getRowId: (row: any) => row.checklist_id,
        }}
        onAddNewClick={() => {
          navigate('add');
        }}
        showDownloadTemplate
        entityName='Maintenance Activities'
        exportData={{
          id: 0,
          activity_name: '',
          pre_requisites: '',
          description: '',
        }}
        isFromActivity={true}
        fileName='maintenance_activities_template'
      />
      <Popup
        open={popup.open}
        icon={<AlertIcon style={{ color: palette.chipWarning.dark }} />}
        message={`Are you sure you want to delete the ${deletableActivity}?`}
        cancel={true}
        confirmation={true}
        cancelLabel='No'
        confirmLabel='Yes'
        onConfirm={() => {
          dispatch(maintenanceManagementActions.deleteActivityRequest(popup.rowId));
          onClosePopup();
        }}
        onClose={onClosePopup}
        onCancel={onClosePopup}
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
  activityListLoading: state.maintenanceManagement.isActivityListLoading,
  activities: state.maintenanceManagement.activityListData,
  activityListTotalCount: state.maintenanceManagement.activityListTotalCount,
  activityListPageNumber: state.maintenanceManagement.activityListPageNumber,
  deleteActivityLoading: state.maintenanceManagement.deleteActivityLoading,
  deleteActivitySuccess: state.maintenanceManagement.deleteActivitySuccess,
});

export default connect(mapStateToProps)(ActivityList);
